import { useEffect, useRef } from 'react'
import { Button, Empty, Input, Skeleton, Tag } from 'antd'
import { RightOutlined, SendOutlined } from '@ant-design/icons'
import { CHAT_SUGGESTIONS, useAssistant } from '../AssistantProvider'
import { Preppy } from '../Preppy'
import type { AssistantSuggestion, PreppyMood } from '../types'

function PreppyBadge({ mood = 'idle' }: { mood?: PreppyMood }) {
  return (
    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center">
      <Preppy mood={mood} size={28} />
      <span className="sr-only">Preppy</span>
    </span>
  )
}

/**
 * The deep-dive chat that AdvicePrep already shipped, now living inside Preppy
 * rather than in its own corner. Behaviour is unchanged and it still talks to
 * the real endpoint — only the message state moved up into the provider so
 * switching tabs mid-conversation does not throw the thread away.
 */
function SuggestionRow({
  suggestion,
  onOpen,
}: {
  suggestion: AssistantSuggestion
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left transition-colors hover:border-[var(--feature-primary)]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[12.5px] font-semibold leading-snug text-gray-900">
            {suggestion.title}
          </span>
          {suggestion.tag && (
            <Tag color={suggestion.tag.colour} className="m-0">
              {suggestion.tag.label}
            </Tag>
          )}
        </div>
        <div className="mt-1 text-[10.5px] text-gray-400">{suggestion.detail}</div>
      </div>
      <RightOutlined className="text-gray-300" />
    </button>
  )
}

export function AnalysisTab() {
  const { state, actions } = useAssistant()
  const {
    messages,
    chatDraft,
    chatSending,
    awaitingReportUpdate,
    hasAnalysis,
    open,
    orgName,
    suggestions,
  } = state

  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages, chatSending])

  // Only grab focus once the panel is actually on screen. The panel stays
  // mounted while closed so it can slide, so an unguarded autofocus would steal
  // the caret on page load from a user who never opened the assistant.
  useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => inputRef.current?.focus(), 250)
    return () => clearTimeout(timer)
  }, [open])

  const disabled = chatSending || awaitingReportUpdate

  // The analysis chat is scoped to one call's report. Off a prep there is
  // nothing for it to read — so rather than an input that sends questions into
  // the void, Preppy offers the meetings it can open, most urgent first.
  if (!hasAnalysis) {
    return (
      <div className="h-full overflow-y-auto px-5 py-5">
        {suggestions.loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : suggestions.items.length > 0 ? (
          <div className="space-y-3">
            <p className="text-[12.5px] leading-relaxed text-gray-600">{suggestions.intro}</p>
            {suggestions.items.map((suggestion) => (
              <SuggestionRow
                key={suggestion.id}
                suggestion={suggestion}
                onOpen={() => actions.openSuggestion(suggestion)}
              />
            ))}
            <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
              {suggestions.note}
            </p>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-[12.5px] leading-relaxed text-gray-500">
                  I read the analysis for one meeting at a time. Open an organisation and pick a
                  meeting, and I can dig into its findings with you.
                </span>
              }
            />
            <Button size="small" onClick={() => actions.openTab('guides')}>
              Show me the guides instead
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
        {/* Rendered live rather than seeded into the thread, so it names the
            organisation as soon as the query resolves. */}
        {messages.length === 0 && (
          <div className="flex gap-2.5" style={{ maxWidth: '92%' }}>
            <PreppyBadge />
            <div className="rounded-xl rounded-tl-sm border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm leading-relaxed text-gray-800">
              Hi, I&rsquo;m Preppy. I&rsquo;ve reviewed the analysis for{' '}
              <b>{orgName || 'this analysis'}</b>. I can dig into any finding, refine the summary, or
              draft talking points for the meeting — and if you&rsquo;d rather be shown than told,
              there are walkthroughs under Guides.
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            style={{ maxWidth: '92%', marginLeft: msg.role === 'user' ? 'auto' : undefined }}
          >
            {msg.role === 'assistant' ? (
              <PreppyBadge />
            ) : (
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-xs font-bold text-gray-600">
                You
              </div>
            )}
            <div
              className={`whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                msg.role === 'assistant'
                  ? 'rounded-tl-sm border border-gray-200 bg-gray-50 text-gray-800'
                  : 'feature-button-solid rounded-tr-sm text-white'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {messages.length === 0 && (
          <div className="ml-10 flex flex-wrap gap-1.5">
            {CHAT_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => void actions.sendMessage(suggestion)}
                className="feature-action-chip cursor-pointer rounded-full px-3 py-1.5 text-xs"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {chatSending && (
          <div className="flex gap-2.5" style={{ maxWidth: '92%' }}>
            <PreppyBadge mood="think" />
            <div
              className="rounded-xl rounded-tl-sm border border-gray-200 bg-gray-50 px-3.5 py-3"
              role="status"
              aria-label="Preppy is thinking"
            >
              <div className="flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-300" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-300" style={{ animationDelay: '150ms' }} />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-300" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      <div className="shrink-0 border-t border-gray-200 px-5 py-4">
        <div className="mb-2 text-xs text-gray-400">
          Reading{' '}
          <span className="rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-600">{orgName}</span>
        </div>
        <div className="flex items-end gap-2">
          <Input.TextArea
            ref={inputRef as never}
            value={chatDraft}
            onChange={(event) => actions.setChatDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void actions.sendMessage(chatDraft)
              }
            }}
            placeholder="Ask about the analysis..."
            aria-label="Ask Preppy about the analysis"
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="flex-1"
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            aria-label="Send message"
            onClick={() => void actions.sendMessage(chatDraft)}
            disabled={!chatDraft.trim() || disabled}
            className="feature-button-solid shrink-0"
          />
        </div>
      </div>
    </div>
  )
}
